import * as React from "react";
import * as yup from "yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Alert,
    Button,
    Checkbox,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Select,
} from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSplitPayment } from "../../../../hooks/mutations/billing";
import useToast from "../../../../hooks/useToast";
import { ISplitPaymentFormBillsPayload } from "../../../../utils/types/billing";
import { useFetchInvoices } from "../../hooks/queries/billing.queries";
import {
    cn,
    formatDate,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { BillStatus, BillStatusLabels } from "../../types/billing.types";
import Loader from "../../../../shared-ui/Loader/Loader";

interface SplitPaymentFormProps {
    paymentId: string;
    onFormSubmit(): void;
    defaultAmount: number;
    invoiceToMoveId: string;
    paymentDate: string;
}

type Options = {
    label: React.ReactNode;
    value: string;
};

export default function SplitPaymentForm({
    paymentId,
    onFormSubmit,
    defaultAmount,
    invoiceToMoveId,
    paymentDate,
}: SplitPaymentFormProps) {
    const [selectedInvoices, setSelectedInvoices] = React.useState<string[]>(
        () => [invoiceToMoveId]
    );
    const [invoiceStatuses, setInvoiceStatuses] = React.useState<string[]>([]);

    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();
    const clientId = params.clientId as string;

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors },
        watch,
        setValue,
    } = useForm<ISplitPaymentFormBillsPayload>({
        resolver: yupResolver(
            yup.object().shape({
                invoices: yup.array().of(
                    yup.object().shape({
                        amount: yup
                            .string()
                            .test(
                                "is-valid-number",
                                "Please enter a valid number",
                                (value) => {
                                    if (!value || value.trim() === "")
                                        return true;

                                    const numValue = parseFloat(value);
                                    return !Number.isNaN(numValue);
                                }
                            )
                            .test(
                                "value-greater-than-zero",
                                "Amount must be greater than zero",
                                (value) => {
                                    if (!value || value.trim() === "")
                                        return true;

                                    const numValue = parseFloat(value);
                                    return (
                                        !Number.isNaN(numValue) && numValue > 0
                                    );
                                }
                            ),
                        invoice_id: yup
                            .string()
                            .required("Invoice ID is required"),
                    })
                ),
                extra_invoices: yup
                    .array()
                    .of(
                        yup.object().shape({
                            amount: yup
                                .string()
                                .test(
                                    "is-valid-number",
                                    "Please enter a valid number",
                                    (value) => {
                                        if (!value || value.trim() === "")
                                            return true;

                                        const numValue = parseFloat(value);
                                        return !Number.isNaN(numValue);
                                    }
                                )
                                .test(
                                    "value-greater-than-zero",
                                    "Amount must be greater than zero",
                                    (value) => {
                                        if (!value || value.trim() === "")
                                            return true;

                                        const numValue = parseFloat(value);
                                        return (
                                            !Number.isNaN(numValue) &&
                                            numValue > 0
                                        );
                                    }
                                ),
                            invoice_id: yup
                                .string()
                                .required("Invoice ID is required"),
                        })
                    )
                    .default([]),
            })
        ),
        mode: "onChange",
    });

    // Mutation hook for splitting payment
    const splitPayment = useSplitPayment();

    const formInvoices = [
        ...(watch("invoices") ?? []),
        ...(watch("extra_invoices") ?? []),
    ];

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const {
        fields: extraInvoices,
        append,
        remove,
    } = useFieldArray({ control, name: "extra_invoices" });

    const handleAddExtraInvoice = React.useCallback(
        (invoiceId: string) => {
            append({
                invoice_id: invoiceId,
                amount: "",
                payment_type: "ci",
            });
        },
        [append]
    );

    const handleRemoveExtraInvoice = React.useCallback(
        (invoiceIndex: number) => {
            remove(invoiceIndex);
        },
        [remove]
    );

    const { data: invoiceData, isLoading: invoiceIsLoading } = useFetchInvoices(
        {
            client_id: clientId,
            payment_view: true,
            invoice_status:
                invoiceStatuses.length === 0 ? undefined : invoiceStatuses,
        }
    );

    const toggleInvoiceSelection = (invoice: string) => {
        if (selectedInvoices.includes(invoice)) {
            setSelectedInvoices(
                selectedInvoices.filter((id) => id !== invoice)
            );
        } else {
            setSelectedInvoices([...selectedInvoices, invoice]);
        }
    };

    const paymentSourcesForSelect = [
        {
            label: "Co insurance",
            value: "ci",
        },
        {
            label: "PR100",
            value: "pr100",
        },
    ];

    const total = formInvoices
        ? Number(
              formInvoices
                  .filter((invoice) => invoice.amount || 0)
                  .reduce(
                      (accumulator, invoice) =>
                          accumulator + Number(invoice.amount),
                      0
                  )
                  .toFixed(2)
          )
        : 0;

    const sortedInvoices = React.useMemo(() => {
        return invoiceData?.data.sort((a, b) => {
            const aPriority = a.invoice_id === invoiceToMoveId ? -1 : 0;
            const bPriority = b.invoice_id === invoiceToMoveId ? -1 : 0;
            return aPriority - bPriority;
        });
    }, [invoiceData?.data, invoiceToMoveId]);

    const getExtraInvoicesForInvoice = React.useMemo(() => {
        return (invoiceId: string) => {
            return extraInvoices.filter((i) => i.invoice_id === invoiceId);
        };
    }, [extraInvoices]);

    const handleSetInvoiceStatuses = (status: string) => {
        if (invoiceStatuses.includes(status)) {
            setInvoiceStatuses(() =>
                invoiceStatuses.filter((IStatus) => IStatus !== status)
            );
        } else {
            setInvoiceStatuses(() => [...invoiceStatuses, status]);
        }
    };

    // function to move payment
    const onSubmit = (payload: ISplitPaymentFormBillsPayload) => {
        if (total !== defaultAmount) {
            toast({
                mode: "error",
                message: `The total amount entered must be equal to ${showMoneyInAppFormat(
                    defaultAmount
                )}`,
            });
            return;
        }

        const filteredInvoices = payload.invoices.filter(
            (invoice) => invoice.amount
        );

        const extraInvoicesArray = payload.extra_invoices
            ? payload.extra_invoices.map((inv) => ({
                  amount: Number(inv.amount),
                  invoice_id: inv.invoice_id,
                  payment_type: inv.payment_type,
              }))
            : [];

        const dataToSend = {
            payment_id: paymentId,
            invoices: filteredInvoices
                .map((invoice) => ({
                    amount: Number(invoice.amount),
                    invoice_id: invoice.invoice_id,
                    payment_type: invoice.payment_type,
                }))
                .concat(extraInvoicesArray),
        };

        splitPayment.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: ["invoices", { client_id: clientId }],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["get-payment"],
                });
                searchParams.delete("is_pr100_split");
                setSearchParams(searchParams);
                toast({
                    mode: "success",
                    message: res.message || "Payment spliting successful",
                });

                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not split payment at this time, please try again later",
                });
            },
        });
    };

    React.useEffect(() => {
        if (sortedInvoices) {
            reset({
                invoices: sortedInvoices?.map((invoice) => ({
                    invoice_id: invoice.invoice_id,
                    amount: "",
                    payment_type: "ci",
                })),
            });
        }
    }, [reset, sortedInvoices]);

    return (
        <form id="move-payment" onSubmit={handleSubmit(onSubmit)}>
            <Alert
                type="info"
                title="Split payment on selected bills"
                description={`The total amount entered across all the bills must be equal to ${showMoneyInAppFormat(
                    defaultAmount
                )}`}
                classNames="mb-[--spacing-header]"
            />

            <div className="mb-[--spacing-items] grid grid-cols-4 gap-x-[--spacing-items] bg-[--card-bg] rounded-md [border:1px_solid_var(--color-stroke)] px-[--padding-card-x] py-[--padding-card-y]">
                <div className="flex flex-col gap-y-2 text-caption">
                    <span className="font-medium uppercase">Payment Date</span>
                    <span className="font-normal">
                        {formatDate(paymentDate)}
                    </span>
                </div>
                <div className="flex flex-col gap-y-2 text-caption">
                    <span className="font-medium uppercase">Total amount</span>
                    <span className="font-normal">
                        {showMoneyInAppFormat(defaultAmount)}
                    </span>
                </div>
                <div className="flex flex-col gap-y-2 text-caption">
                    <span className="font-medium uppercase">
                        Amount inputed
                    </span>
                    <span className="font-normal">
                        {showMoneyInAppFormat(total)}
                    </span>
                </div>
                <div className="flex flex-col gap-y-2 text-caption">
                    <span className="font-medium uppercase">Amount left</span>
                    <span className="font-normal">
                        {showMoneyInAppFormat(
                            Math.abs(defaultAmount) - Math.abs(total)
                        )}
                    </span>
                </div>
            </div>
            <div className="mb-[--spacing-header] flex items-center justify-end">
                {invoiceData && selectedInvoices.length > 0 && (
                    <div className="flex items-center gap-x-2 mr-auto">
                        <span className="text-caption font-medium pr-2 [border-right:1px:solid_var(--color-stroke)]">
                            {selectedInvoices.length} of{" "}
                            {invoiceData?.total_count} sessions selected
                        </span>
                        <Button
                            size="auto"
                            variant="normal"
                            className="text-caption font-medium text-textLink"
                            onClick={() => {
                                setSelectedInvoices([invoiceToMoveId]);
                                reset();
                            }}
                        >
                            Clear all
                        </Button>
                    </div>
                )}
                <Dropdown>
                    <DropdownTrigger asChild>
                        <FilterButton>Bill Status</FilterButton>
                    </DropdownTrigger>
                    <DropdownContent
                        align="end"
                        width="auto"
                        maxHeight={216}
                        className="overflow-y-auto"
                    >
                        {Object.values(BillStatus).map((status) => (
                            <DropdownItem
                                key={status}
                                onClick={() => handleSetInvoiceStatuses(status)}
                                isMulti
                                itemValue={status}
                                values={invoiceStatuses}
                            >
                                {removeEnumUnderscore(BillStatusLabels[status])}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            </div>

            <div className="px-[--padding-card-x] py-[--padding-card-y] rounded-md  border-[0.2px] [border:0.2px_solid_var(--color-stroke)] max-h-[250px] overflow-y-auto">
                {invoiceIsLoading && <Loader />}

                {sortedInvoices?.map((invoice, i) => (
                    <div
                        className="pb-[--spacing-items]"
                        key={invoice.invoice_id}
                    >
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-[--spacing-items]">
                            <div className="flex gap-x-2 items-center text-caption">
                                <Checkbox
                                    name="invoice"
                                    handleChange={() => {
                                        toggleInvoiceSelection(
                                            invoice.invoice_id as string
                                        );
                                        if (
                                            selectedInvoices.includes(
                                                invoice.invoice_id
                                            )
                                        ) {
                                            setValue(
                                                `invoices.${i}.amount`,
                                                ""
                                            );
                                        }
                                    }}
                                    value=""
                                    isChecked={
                                        selectedInvoices.includes(
                                            invoice.invoice_id as string
                                        ) ||
                                        invoiceToMoveId === invoice.invoice_id
                                    }
                                />
                                <span>
                                    {formatDate(invoice.date_of_service)},
                                </span>
                                <span>
                                    CPT: {invoice.cpt_code},{" "}
                                    <span
                                        className={cn({
                                            "text-danger":
                                                invoice.accounting_coinsurance >
                                                0,
                                        })}
                                    >
                                        CINS DUE:{" "}
                                        {invoice.accounting_coinsurance > 0
                                            ? `(${showMoneyInAppFormat(
                                                  invoice.accounting_coinsurance
                                              )})`
                                            : showMoneyInAppFormat(
                                                  Math.abs(
                                                      invoice.accounting_coinsurance
                                                  )
                                              )}
                                    </span>
                                </span>
                            </div>
                            <div
                                className={`flex gap-x-2 justify-end ${
                                    errors.invoices?.[i]?.amount
                                        ? "items-start"
                                        : "items-center"
                                } `}
                            >
                                {selectedInvoices.includes(
                                    invoice.invoice_id
                                ) ? (
                                    <div className="w-[145px]">
                                        <Controller
                                            name={`invoices.${i}.payment_type`}
                                            control={control}
                                            defaultValue={
                                                paymentSourcesForSelect.find(
                                                    (item) =>
                                                        item.value === "ci"
                                                )?.value
                                            }
                                            render={({ field }) => (
                                                <Select
                                                    label=""
                                                    isLongListInDialog
                                                    placeholder="Source(optional)"
                                                    options={
                                                        paymentSourcesForSelect
                                                    }
                                                    onChange={(val) =>
                                                        field.onChange(
                                                            (val as Options)
                                                                .value
                                                        )
                                                    }
                                                    defaultValue={paymentSourcesForSelect.find(
                                                        (item) =>
                                                            item.value === "ci"
                                                    )}
                                                    classNames={{
                                                        container: () =>
                                                            "w-[145px]",
                                                        menuPortal: () =>
                                                            "!z-[50]",
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                ) : null}
                                <div className="w-[145px]">
                                    <Input
                                        {...register(`invoices.${i}.amount`)}
                                        showCurrency
                                        placeholder="Enter Amount"
                                        disabled={
                                            invoiceToMoveId !==
                                                invoice.invoice_id &&
                                            !selectedInvoices.includes(
                                                invoice.invoice_id as string
                                            )
                                        }
                                        className="w-[145px]"
                                        errorText={
                                            errors?.invoices?.[i]?.amount
                                                ?.message
                                        }
                                        max={defaultAmount}
                                        hasError={
                                            !!errors.invoices?.[i]?.amount
                                        }
                                        errorTextClassName="text-[10px]"
                                    />
                                </div>
                                {selectedInvoices.includes(
                                    invoice.invoice_id
                                ) ? (
                                    <Button
                                        variant="normal"
                                        size="auto"
                                        className="h-8 px-2 [border:1px_solid_var(--color-text-link)]"
                                        onClick={() =>
                                            handleAddExtraInvoice(
                                                invoice.invoice_id
                                            )
                                        }
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="size-3.5 stroke-textLink"
                                        >
                                            <line
                                                x1="12"
                                                y1="5"
                                                x2="12"
                                                y2="19"
                                            />
                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>
                                    </Button>
                                ) : null}
                                <input
                                    type="hidden"
                                    value={invoice.invoice_id}
                                    {...register(`invoices.${i}.invoice_id`)}
                                />
                            </div>
                        </div>
                        {getExtraInvoicesForInvoice(invoice.invoice_id)
                            ?.length > 0 ? (
                            <div className="mt-[--spacing-items] flex flex-col gap-y-2">
                                {getExtraInvoicesForInvoice(
                                    invoice.invoice_id
                                ).map((extra) => {
                                    const itemCorrectIndex =
                                        extraInvoices.findIndex(
                                            (ext) => ext.id === extra.id
                                        );

                                    return (
                                        <div
                                            key={extra.id}
                                            className={`flex ${
                                                errors.extra_invoices?.[
                                                    itemCorrectIndex
                                                ]?.amount
                                                    ? "items-start"
                                                    : "items-center"
                                            } items-center gap-x-2 flex-wrap gap-y-3 justify-start md:justify-end`}
                                        >
                                            {selectedInvoices.includes(
                                                invoice.invoice_id
                                            ) ? (
                                                <div className="w-[145px]">
                                                    <Controller
                                                        name={`extra_invoices.${itemCorrectIndex}.payment_type`}
                                                        control={control}
                                                        defaultValue={
                                                            paymentSourcesForSelect.find(
                                                                (item) =>
                                                                    item.value ===
                                                                    "ci"
                                                            )?.value
                                                        }
                                                        render={({ field }) => (
                                                            <Select
                                                                label=""
                                                                isLongListInDialog
                                                                placeholder="Source(optional)"
                                                                options={
                                                                    paymentSourcesForSelect
                                                                }
                                                                onChange={(
                                                                    val
                                                                ) => {
                                                                    field.onChange(
                                                                        (
                                                                            val as Options
                                                                        ).value
                                                                    );
                                                                }}
                                                                defaultValue={paymentSourcesForSelect.find(
                                                                    (item) =>
                                                                        item.value ===
                                                                        "ci"
                                                                )}
                                                                classNames={{
                                                                    container:
                                                                        () =>
                                                                            "w-[145px]",
                                                                    menuPortal:
                                                                        () =>
                                                                            "!z-[50]",
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            ) : null}
                                            <div className="w-[145px]">
                                                <Input
                                                    {...register(
                                                        `extra_invoices.${itemCorrectIndex}.amount`
                                                    )}
                                                    showCurrency
                                                    placeholder="Enter Amount"
                                                    disabled={
                                                        invoiceToMoveId !==
                                                            invoice.invoice_id &&
                                                        !selectedInvoices.includes(
                                                            invoice.invoice_id as string
                                                        )
                                                    }
                                                    className="w-[145px]"
                                                    errorText={
                                                        errors
                                                            ?.extra_invoices?.[
                                                            itemCorrectIndex
                                                        ]?.amount?.message
                                                    }
                                                    max={defaultAmount}
                                                    hasError={
                                                        !!errors
                                                            .extra_invoices?.[
                                                            itemCorrectIndex
                                                        ]?.amount
                                                    }
                                                    errorTextClassName="text-[10px]"
                                                />
                                            </div>
                                            <Button
                                                variant="normal"
                                                size="auto"
                                                className="h-8 px-2 [border:1px_solid_var(--color-danger)]"
                                                onClick={() =>
                                                    handleRemoveExtraInvoice(
                                                        itemCorrectIndex
                                                    )
                                                }
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="size-3.5 stroke-danger"
                                                >
                                                    <line
                                                        x1="18"
                                                        y1="6"
                                                        x2="6"
                                                        y2="18"
                                                    />
                                                    <line
                                                        x1="6"
                                                        y1="6"
                                                        x2="18"
                                                        y2="18"
                                                    />
                                                </svg>
                                            </Button>
                                            <input
                                                type="hidden"
                                                {...register(
                                                    `extra_invoices.${itemCorrectIndex}.invoice_id`
                                                )}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
        </form>
    );
}
